import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader, Paperclip, X } from "lucide-react";
import { AssetPreviewDialog } from "@/components/AssetPreviewDialog";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { AssetSchema } from "@forgepoint/shared";
import { z } from "zod";
import type { OutputAsset } from "@forgepoint/shared";

const UploadResponseSchema = z.object({
  uploadUrl: z.string(),
  asset: AssetSchema,
});

interface NoteAssetUploadProps {
  attachedAssets: OutputAsset[];
  onAssetAdded: (asset: OutputAsset) => void;
  onAssetRemoved: (assetId: string) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  disabled?: boolean;
}

/**
 * Renders an attach button with file picker and staged file chips.
 * Uploads files via the presigned URL flow and reports back OutputAsset objects.
 * Clicking a staged file opens a preview dialog.
 * @param attachedAssets - Currently attached assets to display as chips
 * @param onAssetAdded - Callback when a file finishes uploading
 * @param onAssetRemoved - Callback when a staged asset is removed
 * @param onUploadingChange - Callback when upload state changes
 * @param disabled - Whether the component is disabled
 */
export const NoteAssetUpload: React.FC<NoteAssetUploadProps> = ({
  attachedAssets,
  onAssetAdded,
  onAssetRemoved,
  onUploadingChange,
  disabled = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<OutputAsset | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles file selection, uploads each file via presigned URL, and reports the result.
   * @param event - The file input change event
   */
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    setIsUploading(true);
    onUploadingChange?.(true);

    for (const file of Array.from(files)) {
      try {
        const response = await fetchApi("api/assets/upload", "POST", {
          body: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
        });

        if (!response.ok || !response.data) {
          continue;
        }

        const parsed = UploadResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          continue;
        }

        const uploadResponse = await fetch(parsed.data.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });

        if (!uploadResponse.ok) {
          continue;
        }

        onAssetAdded({
          id: parsed.data.asset.id,
          fileName: parsed.data.asset.fileName,
          fileSize: parsed.data.asset.fileSize,
          fileType: parsed.data.asset.fileType,
          createdAt: parsed.data.asset.createdAt.toISOString(),
        });
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error("Failed to upload file"),
        );
      }
    }

    setIsUploading(false);
    onUploadingChange?.(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
        />
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground transition-all duration-150"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading}
            >
              {isUploading ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Attach file
          </TooltipContent>
        </Tooltip>
      </div>

      {attachedAssets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachedAssets.map((asset) => (
            <div
              key={asset.id}
              className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-md text-sm animate-in fade-in duration-150"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <button
                type="button"
                className="truncate max-w-[200px] hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                title={asset.fileName}
                onClick={() => setPreviewAsset(asset)}
              >
                {asset.fileName}
              </button>
              <button
                type="button"
                className="rounded-sm hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors"
                onClick={() => onAssetRemoved(asset.id)}
                disabled={disabled}
                aria-label={`Remove ${asset.fileName}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <AssetPreviewDialog
        assetId={previewAsset?.id ?? null}
        fileName={previewAsset?.fileName}
        onClose={() => setPreviewAsset(null)}
      />
    </div>
  );
};
